/**
 * Profile-avatar write path — upload the bytes AND the owning `profile_media`
 * row in one operation.
 *
 * WHY THIS EXISTS. The nightly media-reconciliation job (BAL-657, Layer 2)
 * pairs the `profile-media` bucket with the `profile_media` table and treats an
 * object with no owning row as an orphan. Both avatar writers uploaded bytes and
 * never inserted a row, so every avatar older than the 24h grace window read as
 * an orphan and was deleted. The bucket ended up empty.
 *
 * The invariant this module restores: an avatar upload either lands as
 * (object + row) or lands as nothing. `MediaWriteService.uploadAndRecord`
 * removes the object when the row write fails, so there is no partial state.
 *
 * REPLACE. Every avatar for a profile lives under `<uid>/avatar/`, and the
 * filename is derived from the content type, so re-uploading the same format
 * overwrites the same object. Changing format (png → webp) would otherwise
 * leave the old object behind, so after a successful write every OTHER object
 * under that prefix is removed along with its row. Exactly one object and one
 * row survive.
 *
 * Mobile (`apps/mobile/lib/core/data/api/user_api.dart`) implements the same
 * contract in Dart against the same paths. Keep the two in step.
 *
 * @see ./media-write.service.ts (the atomicity primitive)
 * @see ./media-reconciliation.registry.ts (the job this feeds)
 */
import type { SupabaseClient } from '@supabase/supabase-js';

import { Result, ServiceError } from '@kit/shared/result';

import { MediaWriteService } from './media-write.service';
import { StorageBuckets } from './storage-url.service';

/** Every avatar object for a profile lives under this prefix. */
export function profileAvatarPrefix(userId: string): string {
  return `${userId}/avatar`;
}

/**
 * Image content types the avatar writers accept, mapped to the single file
 * extension used for that format. One extension per format keeps the object
 * path deterministic, so a re-upload of the same format overwrites in place.
 */
const AVATAR_EXTENSION_BY_CONTENT_TYPE: Record<string, string> = {
  'image/jpeg': 'jpg',
  'image/jpg': 'jpg',
  'image/png': 'png',
  'image/webp': 'webp',
};

/** The extension this content type is stored under, or null when unsupported. */
export function avatarExtensionForContentType(
  contentType: string,
): string | null {
  return AVATAR_EXTENSION_BY_CONTENT_TYPE[contentType.toLowerCase()] ?? null;
}

/** Deterministic object path for a profile's avatar in a given format. */
export function profileAvatarPath(userId: string, extension: string): string {
  return `${profileAvatarPrefix(userId)}/profile-photo.${extension}`;
}

export interface WriteProfileAvatarParams {
  userId: string;
  body: File | Blob | ArrayBuffer | ArrayBufferView | Buffer | string;
  contentType: string;
}

export interface WriteProfileAvatarResult {
  /** Bucket-relative path of the stored object. */
  path: string;
  /** Public URL for the stored object (`profile-media` is a public bucket). */
  url: string;
}

/**
 * Upload an avatar and record its owning `profile_media` row.
 *
 * Uses the caller's RLS-scoped client: `profile_media`'s insert/update policies
 * require `profile_id = auth.uid()`, and the bucket's storage policy requires
 * the first path segment to be the caller's uid. No admin client is needed.
 */
export async function writeProfileAvatar(
  client: SupabaseClient,
  params: WriteProfileAvatarParams,
): Promise<Result<WriteProfileAvatarResult>> {
  const { userId, body, contentType } = params;

  const extension = avatarExtensionForContentType(contentType);

  if (!extension) {
    return Result.fail(
      ServiceError.validation(
        `Unsupported avatar content type: ${contentType}`,
      ),
    );
  }

  const path = profileAvatarPath(userId, extension);

  const recorded = await new MediaWriteService(client).uploadAndRecord<{
    storage_path: string | null;
    url: string;
  }>({
    bucket: StorageBuckets.PROFILE_MEDIA,
    path,
    body,
    // `upsert` so re-uploading the same format replaces the object in place
    // rather than failing; the row upsert below matches that path exactly.
    uploadOptions: { contentType, upsert: true, cacheControl: '3600' },
    writeRow: async (verifiedPath) => {
      const publicUrl = client.storage
        .from(StorageBuckets.PROFILE_MEDIA)
        .getPublicUrl(verifiedPath).data.publicUrl;

      // `onConflict: 'storage_path'` targets the non-partial unique index added
      // by 20260921090000_profile_media_storage_path_unique.sql. A partial index
      // cannot be inferred here.
      const { data, error } = await client
        .from('profile_media')
        .upsert(
          {
            profile_id: userId,
            type: 'photo',
            storage_path: verifiedPath,
            url: publicUrl,
            thumbnail_url: publicUrl,
            title: 'Profile photo',
            display_order: 0,
            is_featured: false,
            // A fresh object clears any lost-state a previous sweep recorded.
            requires_reupload: false,
            lost_at: null,
            lost_reason: null,
          },
          { onConflict: 'storage_path' },
        )
        .select('storage_path, url')
        .single();

      if (error) {
        return Result.fail(
          ServiceError.database(
            `Failed to record profile avatar: ${error.message}`,
            error,
          ),
        );
      }

      return Result.ok(data);
    },
  });

  if (recorded.isFailure) {
    return Result.fail(recorded.getError());
  }

  await removeSupersededAvatars(client, userId, path);

  return Result.ok({ path, url: recorded.getValue().url });
}

/**
 * Drop every avatar object for this profile other than `keepPath`, and the rows
 * that own them. Best-effort: the new avatar is already durable, and the
 * reconciliation job reports anything left behind, so a failure here must not
 * fail the upload.
 */
async function removeSupersededAvatars(
  client: SupabaseClient,
  userId: string,
  keepPath: string,
): Promise<void> {
  const prefix = profileAvatarPrefix(userId);

  const { data: objects, error } = await client.storage
    .from(StorageBuckets.PROFILE_MEDIA)
    .list(prefix);

  if (error || !objects) return;

  const stalePaths = objects
    .map((object) => `${prefix}/${object.name}`)
    .filter((objectPath) => objectPath !== keepPath);

  if (stalePaths.length === 0) return;

  // Rows first: an object with no row is what the reconciliation job reports,
  // and it is the recoverable direction. A row with no object is the one that
  // renders a broken image to a viewer.
  await client.from('profile_media').delete().in('storage_path', stalePaths);
  await client.storage.from(StorageBuckets.PROFILE_MEDIA).remove(stalePaths);
}
